import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	OnInit,
	AfterViewInit
} from '@angular/core';
import {
	JSONSelection,
	MenuButton,
	MenuItem,
	RenderValueProps,
	TextContent,
	TextSelection,
	renderJSONSchemaEnum,
	renderValue
} from 'vanilla-jsoneditor';
import { BlockType, BuilderService } from '../../../../services/builder.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { VanillaJsonEditorComponent } from '../vanilla-json-editor/vanilla-json-editor.component';
import { MatDialog } from '@angular/material/dialog';
import { ImportFromTripettoDialogComponent } from '../import-from-tripetto-dialog/import-from-tripetto-dialog.component';
import { ConversionResult } from '@nx-ahs/formly/shared';

@Component({
	selector: 'nx-ahs-json-builder',
	templateUrl: './json-builder.component.html',
	styleUrl: './json-builder.component.scss',
	standalone: false
})
/**
 * This component provides a JSON editor interface for constructing,
 * updating, and emitting the JSON model that defines the form.
 */
export class JsonBuilderComponent implements OnInit, AfterViewInit {
	@ViewChild('jsoneditor', { static: false, read: VanillaJsonEditorComponent })
	jsoneditor: VanillaJsonEditorComponent;

	@Input()
	content: any;

	@Input()
	hasChanges: boolean;

	@Input()
	isJsonValid: boolean = true;

	@Output()
	jsonChanged = new EventEmitter<any>(null);

	@Output()
	saveClicked = new EventEmitter<void>(null);

	@Output()
	manageTranslationsClicked = new EventEmitter<void>(null);

	public controlTypes = [];
	public editor: any;
	public jsonFieldsReady = false;

	private jsonSelection: any;
	private selectionRange: { start: number; end: number } | null = null;

	constructor(
		private builderService: BuilderService,
		private clipboard: Clipboard,
		private chgDetector: ChangeDetectorRef,
		private dialog: MatDialog
	) {}

	ngOnInit() {}

	ngAfterViewInit() {
		this.editor = this.jsoneditor.editor;
	}

	public jsonHasChanged($event) {
		this.jsonChanged.emit($event);
	}

	public onManageTranslationsClicked(): void {
		this.manageTranslationsClicked.emit();
	}

	public editorOnSelect($event): void {
		this.jsonSelection = $event;

		if (!this.jsonSelection?.path) {
			const textSelection: TextSelection = $event;
			this.selectionRange = {
				start: textSelection.ranges[0].anchor,
				end: textSelection.ranges[0].head
			};
		}
	}

	public blockTypeSelected($event: BlockType): void {
		const json = $event.click();
		this.insertIntoJsonWithRange(json);
		this.chgDetector.detectChanges();
		//this.insertAtCursor(JSON.stringify(json));
	}

	public save() {
		this.saveClicked.emit();
	}

	public importFromTripetto(): void {
		const dialogRef = this.dialog.open(ImportFromTripettoDialogComponent, {
			width: '800px',
			maxHeight: '90vh',
			data: {}
		});

		dialogRef.afterClosed().subscribe((result: ConversionResult) => {
			if (result?.formlyConfig) {
				// Update the JSON editor with converted config
				this.editor.set({ json: result.formlyConfig });

				// Emit change event
				this.jsonChanged.emit(result.formlyConfig);

				// Log success message with warning information
				if (result.warnings.length > 0) {
					const warningCount = result.warnings.filter((w) => w.severity === 'warning').length;
					const errorCount = result.warnings.filter((w) => w.severity === 'error').length;

					let message = `Import successful. `;
					if (errorCount > 0) {
						message += `${errorCount} error(s), `;
					}
					if (warningCount > 0) {
						message += `${warningCount} warning(s). `;
					}
					message += `Please review the converted script.`;

					console.log(message);
				} else {
					console.log(`Successfully imported ${result.formlyConfig.length} field(s) from Tripetto.`);
				}
			}
		});
	}

	private insertIntoJsonWithRange(json) {
		let jsonContent: any = this.editor.get();

		if (!this.jsonSelection?.path) {
			let jsonStrToInsert;
			let currentText: string = null;

			if (typeof json === 'object' && json !== null) {
				jsonStrToInsert = JSON.stringify(json, null, 4);
			} else {
				jsonStrToInsert = json;
			}

			if (jsonContent?.text) {
				currentText = jsonContent.text;
			} else {
				currentText = JSON.stringify((jsonContent as any).json, null, 4);
			}

			// Grab text before and after the selected range
			const beforeText = currentText.slice(0, this.selectionRange.start);
			const afterText = currentText.slice(this.selectionRange.end);

			// Trim trailing whitespace on beforeText so you can reliably check the last character
			const trimmedBeforeText = beforeText.replace(/\s+$/, '');
			const charBefore = trimmedBeforeText.length > 0 ? trimmedBeforeText[trimmedBeforeText.length - 1] : '';

			// Decide whether or not to add a comma
			const isStartOfFile = this.selectionRange.start === 0;
			const isBracketOrComma = charBefore === '[' || charBefore === '{' || charBefore === ',';
			const shouldAddComma = !(isStartOfFile || isBracketOrComma);

			// Build the new text
			const newText = beforeText + (shouldAddComma ? ',' : '') + jsonStrToInsert + afterText;

			this.editor.update({ text: newText });
		} else {
			if ((this.jsonSelection as any)?.path) {
				const path = (this.jsonSelection as any)?.path;
				let jsonToInsert;
				let currentJson: string = null;

				if (typeof json === 'object' && json !== null) {
					jsonToInsert = json;
				} else {
					jsonToInsert = JSON.parse(json);
				}

				if (jsonContent?.text) {
					currentJson = JSON.parse(jsonContent.text);
				} else {
					currentJson = (jsonContent as any).json;
				}

				// Navigate to the selected path and insert new data
				let currentNode: any = currentJson;

				for (let i = 0; i < path.length - 1; i++) {
					currentNode = currentNode[path[i]];
				}

				const lastKey = path[path.length - 1];

				// Insert new JSON at the selected path
				if (Array.isArray(currentNode)) {
					// If the node is an array, insert the new JSON at the index
					currentNode.splice(parseInt(lastKey, 10), 0, jsonToInsert);
				} else if (typeof currentNode === 'object') {
					// If the node is an object, insert it as a new key
					currentNode[lastKey] = jsonToInsert;
				}

				// Update the editor with the modified JSON
				this.editor.set({ json: currentNode });
			}
		}

		jsonContent = this.editor.get();
		if (jsonContent.json) {
			this.jsonChanged.emit(jsonContent.json);
		} else {
			this.jsonChanged.emit(JSON.parse(jsonContent.text));
		}
	}
}
